import { useEffect, useMemo, useState } from 'react';

import { formatDateToYMD } from '@shared/utils/formatDate';
import useAuthStore from '@entities/auth/model/useAuthStore';
import { getEmployeeInfo } from '@entities/employee/api/employees';
import {
  useGetVacationCheckApprover,
  useGetVacationEventType,
  useSaveVacationFileMutation,
  useSaveVacationMutation,
  useVacationTypesQuery,
} from '@features/vacation/hooks';
import { checkVacationOverlap } from '@features/vacation/utils/checkVacationOverlap';
import { useHolidayListQuery } from '@features/holiday/hooks';

export function useVacationRequestForm({ isOpen, onClose, onSuccess, existingVacations = [] }) {
  const today = new Date();
  const { enumber, user } = useAuthStore();

  /* ======================
   * form data
   ====================== */
  const [formData, setFormData] = useState({
    vacationType: '1',
    vacationDivision: 'A01',
    ceremonyType: '',
    ceremonyTarget: '',
    dueDate: today,
    startDate: today,
    endDate: today,
    period: 1,
    recipients: [],
    reason: '',
    file: null,
  });

  /* ======================
   * 사용자 정보
   ====================== */
  const [employeeInfo, setEmployeeInfo] = useState(null);

  useEffect(() => {
    ``;
    if (!enumber || !isOpen) return;

    getEmployeeInfo(enumber)
      .then((res) => {
        setEmployeeInfo(res?.resultData || res);
      })
      .catch(() => {
        setEmployeeInfo(null);
      });
  }, [enumber, isOpen]);

  /* ======================
   * 휴가 종류
   ====================== */
  const { data: vacationTypes = [], isLoading: isLoadingVacationTypes } = useVacationTypesQuery();

  const selectedVacationTypeCode = vacationTypes.find(
    (type) => type.code.toString() === formData.vacationType,
  )?.code;

  /* ======================
   * 경조 타입
   ====================== */
  const isCeremonyType = selectedVacationTypeCode === 5;

  const { data: ceremonyTypesData } = useGetVacationEventType(
    isCeremonyType ? selectedVacationTypeCode : null,
    { enabled: isCeremonyType },
  );

  const ceremonyTypes =
    ceremonyTypesData?.resultData?.vacationEventTypeList ||
    ceremonyTypesData?.vacationEventTypeList ||
    [];

  /* ======================
   * 결재자
   ====================== */
  const { data: approverData } = useGetVacationCheckApprover();
  const signerEnumber = approverData?.resultData?.enumber ?? null;

  /* ======================
   * UI 제어 상태
   ====================== */
  const { showVacationDivision, showDueDate, showCeremonyFields } = useMemo(() => {
    const vacationType = formData.vacationType;

    return {
      showVacationDivision: ['1', '4', '7'].includes(vacationType),
      showDueDate: vacationType === '2',
      showCeremonyFields: vacationType === '5',
    };
  }, [formData.vacationType]);
  /**
   * 현재년도 계산
   */
  const currentYear = useMemo(() => {
    const start = new Date(formData.startDate);
    return isNaN(start.getTime()) ? new Date().getFullYear() : start.getFullYear();
  }, [formData.startDate]);
  //공휴일 조회
  const { data: holidays = [] } = useHolidayListQuery(currentYear);
  const holidaySet = useMemo(() => {
    const set = new Set();
    holidays.forEach((h) => {
      set.add(h.holidayDate);
    });
    return set;
  }, [holidays]);

  /* ======================
   * 휴가 기간 자동 계산
   ====================== */
  //휴가기간 계산시 주말 과 공휴일 제외
  function countWorkingDays(startDate, endDate, holidaySet = new Set()) {
    if (!startDate || !endDate) return 0;

    const start = new Date(startDate);
    const end = new Date(endDate);

    if (start > end) return 0;

    let count = 0;
    const current = new Date(start);

    while (current <= end) {
      const day = current.getDay();
      const formatted = formatDateToYMD(current);

      const isWeekend = day === 0 || day === 6;
      const isHoliday = holidaySet.has(formatted);

      if (!isWeekend && !isHoliday) {
        count++;
      }

      current.setDate(current.getDate() + 1);
    }

    return count;
  }

  //주말포함 날짜 계산
  function countAllDays(startDate, endDate) {
    if (!startDate || !endDate) return 0;

    const start = new Date(startDate);
    const end = new Date(endDate);

    if (start > end) return 0;

    return Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
  }

  useEffect(() => {
    const start = new Date(formData.startDate);
    const end = new Date(formData.endDate);

    if (isNaN(start.getTime()) || isNaN(end.getTime())) return;

    const startDate = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    const endDate = new Date(end.getFullYear(), end.getMonth(), end.getDate());

    let nextEndDate = formData.endDate;

    const isCeremony = showCeremonyFields && formData.ceremonyType;
    const isSpouseBirth = isCeremony && formData.ceremonyType === 'Z01';
    const allowWeekend = isCeremony && !isSpouseBirth;

    // const diffDays =
    //   Math.floor((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;

    // let period = diffDays;
    let period = allowWeekend
      ? countAllDays(startDate, endDate)
      : countWorkingDays(startDate, endDate, holidaySet);
    // 반차 처리
    if (
      showVacationDivision &&
      (formData.vacationDivision === 'A02' || formData.vacationDivision === 'A03')
    ) {
      nextEndDate = formData.startDate;
      period = 0.5;
    }

    // 변경 사항이 있을 때만 setState
    if (period !== formData.period || nextEndDate !== formData.endDate) {
      setFormData((prev) => ({
        ...prev,
        endDate: nextEndDate,
        period,
      }));
    }
  }, [
    formData.startDate,
    formData.endDate,
    formData.vacationDivision,
    showVacationDivision,
    holidaySet,
  ]);

  /* ======================
   * 휴가 종류 변경
   ====================== */
  function handleVacationTypeChange(value) {
    setFormData((prev) => ({
      ...prev,
      vacationType: value,
      vacationDivision: 'A01',
      ceremonyType: '',
      ceremonyTarget: '',
      dueDate: today,
      startDate: today,
      endDate: today,
      period: 1,
      recipients: [],
    }));
  }

  /* ======================
   * 파일 처리
   ====================== */
  function handleFileChange(file) {
    setFormData((prev) => ({ ...prev, file }));
  }

  function handleFileRemove() {
    setFormData((prev) => ({ ...prev, file: null }));
  }

  /* ======================
   * 저장
   ====================== */
  const saveMutation = useSaveVacationMutation();
  const saveFileMutation = useSaveVacationFileMutation();

  async function handleSave() {
    if (!formData.vacationType) {
      throw new Error('휴가 종류를 선택해주세요.');
    }

    if (!formData.startDate || !formData.endDate) {
      throw new Error('휴가 일자를 선택해주세요.');
    }

    if (!signerEnumber) {
      throw new Error('결재자를 찾을 수 없습니다.');
    }

    if (!formData.reason || !formData.reason.trim()) {
      throw new Error('신청 사유를 입력해주세요.');
    }
    //일자 중복체크 훅으로 이동
    if (
      checkVacationOverlap({
        formData,
        existingVacations,
      })
    ) {
      throw new Error('이미 해당 날짜에 휴가 일정이 있어 신청이 불가합니다.');
    }

    /* ======================
   * 신규 cc_type 계산 
   ====================== */
    const newCcType =
      showCeremonyFields && formData.ceremonyType
        ? formData.ceremonyType
        : formData.vacationDivision;

    const params = {
      idx: 0,
      vacation_type: formData.vacationType,
      start_date: formatDateToYMD(new Date(formData.startDate)),
      end_date: formatDateToYMD(new Date(formData.endDate)),
      // period: formData.period,
      reason: formData.reason,
      signer_enumber: signerEnumber,
      // 기존 저장용 cc_type
      cc_type: newCcType,

      // 백엔드 중복 체크용
      newCcType: newCcType,
    };

    if (showDueDate && formData.dueDate) {
      params.due_date = formatDateToYMD(new Date(formData.dueDate));
    }

    if (Array.isArray(formData.recipients) && formData.recipients.length > 0) {
      const recipients = formData.recipients.map((r) => r?.enumber).filter(Number.isInteger);

      if (recipients.length > 0) {
        params.recipients = recipients.join(',');
      }
    }

    const result = await saveMutation.mutateAsync(params);

    if (result?.result_code !== 0) {
      throw new Error(result?.error_msg || '휴가 신청에 실패했습니다.');
    }

    // todo 모바일 갱신

    // 비즈니스 에러 처리 (result_code가 0이 아니면 에러로 간주)
    if (result?.result_code !== 0) {
      throw new Error(result?.error_msg || '휴가 신청에 실패했습니다.');
    }

    const vacationIdx = result?.resultData?.idx;

    if (formData.file && vacationIdx) {
      await saveFileMutation.mutateAsync({
        file: formData.file,
        vacationIdx,
      });
    }

    onClose?.();
    onSuccess?.();
  }

  /* ======================
   * 모달 열릴 때 초기화
   ====================== */
  useEffect(() => {
    if (!isOpen) return;

    setFormData({
      vacationType: '1',
      vacationDivision: 'A01',
      ceremonyType: '',
      ceremonyTarget: '',
      dueDate: today,
      startDate: today,
      endDate: today,
      period: 1,
      recipients: [],
      reason: '',
      file: null,
    });
  }, [isOpen]);

  /* ======================
   * 경조 휴가 종료일 자동 계산
   * ====================== */
  useEffect(() => {
    if (!showCeremonyFields) return;
    if (!formData.ceremonyType || !formData.startDate) return;

    const start = new Date(formData.startDate);
    if (isNaN(start.getTime())) return;

    let daysToAdd = 0;
    const type = formData.ceremonyType;

    if (/^A01$/.test(type)) daysToAdd = 7;
    else if (/^A0([2-4])$/.test(type) || /^B0[1-2]$/.test(type) || /^C01$/.test(type))
      daysToAdd = 1;
    else if (/^D0[1-5]$/.test(type)) daysToAdd = 5;
    else if (/^D(06|07|08|09|10)$/.test(type)) daysToAdd = 3;
    else if (/^Z01$/.test(type)) daysToAdd = 20;

    // 1일 휴가(daysToAdd가 0이나 1)인 경우에도 시작일과 종료일을 동기화
    const end = new Date(start);
    if (daysToAdd > 1) {
      end.setDate(end.getDate() + daysToAdd - 1);
    }

    const endDateStr = formatDateToYMD(end);

    // 불필요한 setState 방지
    if (formData.endDate !== endDateStr) {
      setFormData((prev) => ({
        ...prev,
        endDate: endDateStr,
      }));
    }
  }, [formData.startDate, formData.ceremonyType, showCeremonyFields]);

  /* ======================
   * 반환
   ====================== */
  return {
    formData,
    setFormData,

    vacationTypes,
    ceremonyTypes,

    showVacationDivision,
    showDueDate,
    showCeremonyFields,

    isLoadingVacationTypes,

    employeeInfo,
    applicantInfo: employeeInfo
      ? `${employeeInfo.name || user?.name || ''} / ${employeeInfo.team || ''}`
      : '',

    handleVacationTypeChange,
    handleFileChange,
    handleFileRemove,
    handleSave,

    saveMutation,
    saveFileMutation,
    holidaySet,
  };
}
